/**
 * specimen.tsx — the shared instrument frame and widget contract.
 *
 * Extracted from Primitive.tsx so BOTH the generic widgets and the typed
 * business-operating-model widgets (business/methods.tsx) render inside the same
 * specimen card, reflective echo, evidence block, and footer — drawn from
 * egae-component-library §01 — without a circular import between the two files.
 */
import { createContext, useContext } from "react";
import type { ReactNode } from "react";
import type { PrimitiveDefinition } from "../../domain/primitives.ts";
import type { QuestionStimulus } from "../../ai/provider.ts";
import type { BlockData } from "../../genui/blocks.ts";
import type { Workspace } from "../../store/events.ts";
import { LoadPips } from "../lib/format.tsx";
import { Markdown } from "../lib/markdown.tsx";
import { StaticBlocks } from "../genui/blocks.tsx";
import { parseBlock } from "../../genui/blocks.ts";
import { useT } from "../lib/i18n.tsx";

/** Validate AI-supplied blocks through arktype, keeping only the well-formed ones —
 *  so a flaky model can never hand the renderer a malformed block. */
export function safeBlocks(blocks: readonly BlockData[] | undefined): BlockData[] {
  if (!blocks) return [];
  const out: BlockData[] = [];
  for (const b of blocks) {
    const r = parseBlock(b);
    if (r.ok) out.push(r.data);
  }
  return out;
}

export interface EmitPayload {
  summary: string;
  boundary: string;
  rawContent: Record<string, unknown>;
}

export interface WidgetProps {
  primitive: PrimitiveDefinition;
  emit: (p: EmitPayload) => void;
  /** AI-generated question that replaces the primitive's default prompt. */
  promptOverride?: string;
  /** Prior rule/stance the instrument works on (laddering climbs up from it). */
  seed?: string | undefined;
  /** AI-generated title/body/answer-content the instrument renders (topic-fitting). */
  stimulus?: QuestionStimulus | undefined;
  /** The workspace, so canvas/context blocks can resolve entity references. */
  ws: Workspace;
}

/** Shared by every widget's <Specimen> so the card renders the AI question consistently. */
export const StimulusCtx = createContext<{ stimulus?: QuestionStimulus | undefined; ws: Workspace } | null>(null);

export function boundaryFor(p: PrimitiveDefinition): string {
  return `${p.evidenceOutput.join(" + ")} · ${p.validityRisks[0] ?? "single observation"} · review required`;
}

export function firstSentence(t: string): string {
  return (t.trim().split(/(?<=[.!?])\s/)[0] ?? t.trim()).slice(0, 160);
}

/** The specimen card frame shared by every primitive. Renders the question as a short
 *  title + a single markdown sentence (the recommended default), with optional
 *  consumption-only GenUI context blocks above the answer controls. */
export function Specimen({ p, children, echo, evidence }: { p: PrimitiveDefinition; children: ReactNode; echo?: ReactNode; evidence?: ReactNode }): JSX.Element {
  const t = useT();
  const ctx = useContext(StimulusCtx);
  const stimulus = ctx?.stimulus;
  const body = (stimulus?.body && stimulus.body.trim()) || p.prompt;
  const context = safeBlocks(stimulus?.context);
  return (
    <div className="specimen">
      <div className="spec-tab">
        <span className="spec-id">
          <b>{p.slug}</b> · {p.methodFamily[0]}
        </span>
        <span className="spec-fam">{p.interactionShape}</span>
      </div>
      <div className="spec-body">
        <div className="spec-q">
          <span className="by">{p.primitiveId}</span>
          {stimulus?.title ? <div className="spec-title">{stimulus.title}</div> : null}
          <Markdown source={body} className="spec-q-md" />
        </div>
        {context.length > 0 && ctx ? (
          <div className="spec-context">
            <StaticBlocks ws={ctx.ws} blocks={context} />
          </div>
        ) : null}
        {children}
        {echo}
        {evidence}
        <div className="spec-foot">
          <div className="foot-item">
            <div className="foot-k">{t("prim.construct")}</div>
            <div className="foot-v">{p.constructTargets.join(" · ")}</div>
          </div>
          <div className="foot-item">
            <div className="foot-k">{t("prim.load")}</div>
            <div className="foot-v">
              <LoadPips level={p.cognitiveLoadBudget.expectedDemand} />
            </div>
          </div>
          <div className="foot-item">
            <div className="foot-k">{t("prim.output")}</div>
            <div className="foot-v">{p.evidenceOutput.join(", ")}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function Echo({ children }: { children: ReactNode }): JSX.Element {
  const t = useT();
  return (
    <div className="echo">
      <span className="tag">{t("prim.echo.tag")}</span>
      {children}
    </div>
  );
}
